/**
 * Embedding Model Select Component
 *
 * Controlled select dropdown for embedding model selection with model details.
 * Groups models by provider type dynamically.
 */

import { cn } from '@/lib/utils';
import {
  embeddingProviderLabel,
  type EmbeddingModelOption,
} from '@/hooks/use-embedding-models';

export interface EmbeddingModelSelectProps {
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
  className?: string;
  /** Available embedding model options. Uses empty array if not provided. */
  options?: EmbeddingModelOption[];
  /** Show loading state instead of the select. */
  isLoading?: boolean;
  /** Label for the empty (platform default) option. */
  platformDefaultLabel?: string;
}

/**
 * Controlled select for embedding model selection.
 * Groups models by provider type.
 */
export function EmbeddingModelSelect({
  value,
  onChange,
  disabled = false,
  className,
  options = [],
  isLoading = false,
  platformDefaultLabel = '— Platform default',
}: EmbeddingModelSelectProps) {
  if (isLoading) {
    return (
      <div className={cn(
        'w-full px-3 py-2 text-sm border rounded-md',
        'border-gray-300 dark:border-gray-600',
        'bg-gray-50 dark:bg-gray-900',
        'text-gray-400 dark:text-gray-500',
        className
      )}>
        Loading models...
      </div>
    );
  }

  // Group options by provider_type
  const grouped = options.reduce<Record<string, EmbeddingModelOption[]>>((acc, opt) => {
    (acc[opt.provider_type] ??= []).push(opt);
    return acc;
  }, {});

  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
      className={cn(
        'w-full px-3 py-2 text-sm border rounded-md',
        'border-gray-300 dark:border-gray-600',
        'bg-white dark:bg-gray-800',
        'text-gray-900 dark:text-gray-100',
        'disabled:bg-gray-50 dark:disabled:bg-gray-900',
        'disabled:text-gray-400 dark:disabled:text-gray-600',
        'disabled:cursor-not-allowed',
        'focus:ring-2 focus:ring-blue-500 focus:ring-offset-0',
        'dark:focus:ring-blue-400',
        className
      )}
    >
      <option value="">
        {platformDefaultLabel}
      </option>

      {Object.entries(grouped).map(([providerType, models]) => (
        <optgroup key={providerType} label={embeddingProviderLabel(providerType)}>
          {models.map((model) => (
            <option key={model.model_id} value={model.model_id}>
              {model.display_name}{model.dimensions ? ` (${model.dimensions} dims)` : ''}{model.is_default ? ' ★' : ''}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

export default EmbeddingModelSelect;
